import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, ChevronRight, Plus } from 'lucide-react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import { useData } from '../../context/DataContext';

// Container component
const Container = ({ children, className = "" }) => (
    <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

// Helper to generate product slug
const generateBrandedProductSlug = (brand, name) => {
  return `${brand}-${name}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Product Row Component - matches category/subcategory styling
const ProductRow = ({ product }) => {
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={`full-${i}`} className="text-[#ab3d22]">★</span>);
    }
    if (hasHalfStar) {
      stars.push(<span key="half" className="text-[#ab3d22]">☆</span>);
    }
    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<span key={`empty-${i}`} className="text-gray-300">☆</span>);
    }
    return stars;
  };

  return (
    <article 
      className="rounded-2xl border border-gray-300 bg-white shadow-md hover:shadow-lg transition-all duration-300"
    >
      {/* Main Product Row */}
      <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_auto_180px] items-center gap-4 md:gap-6 p-4 md:p-6">
        {/* Product Image */}
        <div className="bg-white rounded-xl p-2 mx-auto md:mx-0">
          <img 
            src={product.imageUrl} 
            alt={product.name} 
            className="h-24 w-24 md:h-28 md:w-28 object-contain" 
          />
        </div>

        {/* Product Info */}
        <div className="min-w-0">
          <div className="mb-1">
            <span className="text-xs uppercase tracking-wide text-[#ab3d22] font-medium">
              {product.brand}
            </span>
          </div>
          
          <Link
            to={`/product/${generateBrandedProductSlug(product.brand, product.name)}`}
            className="hover:text-[#ab3d22] transition-colors"
          >
            <h4 className="text-base md:text-lg font-semibold text-neutral-900 line-clamp-2">
              {product.name}
            </h4>
          </Link>
          
          <p className="mt-1 text-sm text-neutral-600 line-clamp-1">
            {typeof product.category === 'string' ? product.category : product.category?.name || 'Uncategorized'}
            {product.subcategory && ` • ${typeof product.subcategory === 'string' ? product.subcategory : product.subcategory?.name}`}
          </p>

          {/* Badges */}
          {product.badges && product.badges.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {product.badges.map((badge) => (
                <span 
                  key={badge} 
                  className="inline-flex items-center rounded-full bg-[#ab3d22]/10 text-[#ab3d22] border border-[#ab3d22]/30 px-2 py-0.5 text-xs font-medium"
                >
                  {badge.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Rating Stats */}
        <div className="flex flex-col items-center gap-2 px-4 py-3 bg-gray-50/50 rounded-xl border border-gray-100 min-w-[140px]">
          <div className="flex items-center gap-1">
            {renderStars(product.rating || 0)}
          </div>
          <div className="text-lg font-semibold text-neutral-900">
            {(product.rating || 0).toFixed(1)}
          </div>
          <div className="text-xs text-neutral-600">
            {product.reviewCount || 0} {product.reviewCount === 1 ? 'review' : 'reviews'}
          </div>
          {/* Repurchase % - will be populated from community reviews */}
          {product.repurchasePercent && (
            <div className="text-xs font-medium text-[#ab3d22]">
              {product.repurchasePercent}% repurchase
            </div>
          )}
        </div>

        {/* CTAs */}
        <div className="flex flex-col gap-2">
          <a 
            href={product.affiliates?.[0]?.url || product.affiliateLink || '#'} 
            target="_blank" 
            rel="nofollow noopener sponsored" 
            className="inline-flex items-center justify-center rounded-xl bg-[#ab3d22] hover:bg-[#8a2f1a] px-4 py-2.5 text-sm font-medium text-white hover:shadow-md transition-all duration-200 shadow-sm text-center"
            data-cta="shop_now"
            data-product={generateBrandedProductSlug(product.brand, product.name)}
            data-category={typeof product.category === 'object' ? product.category?.slug : (product.category || '').toLowerCase().replace(/\s+/g, '-')}
          >
            Shop Now
          </a>
          <Link
            to={`/product/${generateBrandedProductSlug(product.brand, product.name)}`}
            className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-gray-300 text-gray-700 hover:bg-black hover:border-black hover:text-white px-4 py-2.5 text-sm font-medium transition-all duration-200 text-center"
          >
            <span>See Full Reviews</span>
          </Link>
        </div>
      </div>
    </article>
  );
};

const AllProductsPage = () => {
    const { products, categories: allCategories, subcategories: allSubcategories } = useData();
    const location = useLocation();
    const navigate = useNavigate();
    
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [subcategoryFilter, setSubcategoryFilter] = useState('all');
    const [brandFilter, setBrandFilter] = useState('all');
    const [sortBy, setSortBy] = useState('rating');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // Check for search query and brand filter from URL
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const searchQuery = urlParams.get('search') || location.state?.searchQuery;
        const brandQuery = urlParams.get('brand');
        
        if (searchQuery) {
            setSearchTerm(searchQuery);
        }
        if (brandQuery) {
            setBrandFilter(brandQuery);
        }
    }, [location]);

    // Extract unique brands - filtered by category/subcategory selection
    const brands = useMemo(() => {
        if (!products || !Array.isArray(products)) return [];
        
        let filteredForBrands = products.filter(p => p.isActive);
        
        // Filter by category if selected
        if (categoryFilter !== 'all') {
            filteredForBrands = filteredForBrands.filter(product => {
                const productCategory = typeof product.category === 'string' 
                    ? product.category 
                    : product.category?.name;
                return productCategory === categoryFilter;
            });
        }
        
        // Filter by subcategory if selected
        if (subcategoryFilter !== 'all') {
            filteredForBrands = filteredForBrands.filter(product => {
                const productSubcategory = typeof product.subcategory === 'string' 
                    ? product.subcategory 
                    : product.subcategory?.name;
                return productSubcategory === subcategoryFilter;
            });
        }
        
        const brandMap = new Map();
        filteredForBrands.forEach(product => {
            if (product.brand) {
                const brandName = product.brand.trim();
                if (!brandMap.has(brandName)) {
                    brandMap.set(brandName, brandName);
                }
            }
        });
        return Array.from(brandMap.values()).sort();
    }, [products, categoryFilter, subcategoryFilter]);

    // Get subcategories for selected category
    const filteredSubcategories = useMemo(() => {
        if (categoryFilter === 'all') return allSubcategories || [];
        return (allSubcategories || []).filter(sub => {
            const subCat = typeof sub.category === 'string' ? sub.category : sub.category?.name;
            return subCat === categoryFilter;
        });
    }, [categoryFilter, allSubcategories]);

    // Reset brand filter if it's no longer in the available brands
    useEffect(() => {
        if (brandFilter !== 'all' && !brands.includes(brandFilter)) {
            setBrandFilter('all');
        }
    }, [brands, brandFilter]);

    // Filter and sort products
    const filteredProducts = useMemo(() => {
        let filtered = products.filter(p => p.isActive);

        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(product => {
                const brandMatch = product.brand?.toLowerCase().includes(searchLower);
                const nameMatch = product.name?.toLowerCase().includes(searchLower);
                return brandMatch || nameMatch;
            });
        }

        // Category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(product => {
                const productCategory = typeof product.category === 'string' 
                    ? product.category 
                    : product.category?.name;
                return productCategory === categoryFilter;
            });
        }

        // Subcategory filter
        if (subcategoryFilter !== 'all') {
            filtered = filtered.filter(product => {
                const productSubcategory = typeof product.subcategory === 'string' 
                    ? product.subcategory 
                    : product.subcategory?.name;
                return productSubcategory === subcategoryFilter;
            });
        }

        // Brand filter
        if (brandFilter !== 'all') {
            filtered = filtered.filter(product => product.brand === brandFilter);
        }

        // Sort
        switch (sortBy) {
            case 'rating':
                filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
                break;
            case 'reviews':
                filtered.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
                break;
            case 'repurchase':
                filtered.sort((a, b) => (b.repurchasePercent || 0) - (a.repurchasePercent || 0));
                break;
            case 'recent':
                filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
                break;
            case 'name':
                filtered.sort((a, b) => a.name.localeCompare(b.name));
                break;
            default:
                break;
        }

        return filtered;
    }, [products, searchTerm, categoryFilter, subcategoryFilter, brandFilter, sortBy]);

    // Pagination
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const paginatedProducts = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredProducts.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredProducts, currentPage]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, categoryFilter, subcategoryFilter, brandFilter, sortBy]);

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setCategoryFilter('all');
        setSubcategoryFilter('all');
        setBrandFilter('all');
        setSortBy('rating');
    };

    // REMOVED: Loading blocker - page renders instantly
    // The empty state is handled in the products list section below

    return (
        <div className="min-h-screen bg-white">
            <Navbar />
            
            {/* Header Hero */}
            <header className="relative w-full bg-[#f8f8f8] overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-30" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c73215' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }}></div>
                
                <div className="relative mx-auto max-w-7xl px-4 py-6 sm:py-8 md:py-12">
                    <nav className="mb-3 sm:mb-4 text-xs sm:text-sm text-gray-600" aria-label="Breadcrumb">
                        <ol className="flex flex-wrap items-center gap-1 sm:gap-2">
                            <li className="flex items-center gap-1 sm:gap-2">
                                <Link to="/" className="hover:text-[#ab3d22] transition-colors">Home</Link>
                                <ChevronRight className="h-3 w-3 text-gray-400" />
                            </li>
                            <li className="flex items-center gap-1 sm:gap-2">
                                <span className="text-gray-900">Product Reviews</span>
                            </li>
                        </ol>
                    </nav>
                    
                    <div className="mt-4">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-3" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                            Discover & Review Every Product on Radiance
                        </h1>
                        <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed max-w-4xl">
                            Search thousands of beauty and wellness products. Filter by brand, category, or skin type — and see what real people think before you buy.
                        </p>
                    </div>
                </div>
            </header>

            {/* Filters Section - Sticky */}
            <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
                <Container>
                    <div className="py-4">
                        {/* Search Bar */}
                        <div className="mb-4">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by brand or product name..."
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ab3d22] focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Filter Row */}
                        <div className="flex flex-wrap gap-3 items-center justify-between">
                            <div className="flex flex-wrap gap-3 flex-1">
                                {/* Category Filter */}
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => {
                                        setCategoryFilter(e.target.value);
                                        setSubcategoryFilter('all'); // Reset subcategory
                                    }}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ab3d22] focus:border-transparent bg-white"
                                >
                                    <option value="all">All Categories</option>
                                    {(allCategories || []).map(cat => (
                                        <option key={cat._id || cat.name} value={cat.name}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>

                                {/* Subcategory Filter */}
                                <select
                                    value={subcategoryFilter}
                                    onChange={(e) => setSubcategoryFilter(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ab3d22] focus:border-transparent bg-white"
                                    disabled={categoryFilter === 'all'}
                                >
                                    <option value="all">All Subcategories</option>
                                    {filteredSubcategories.map(sub => (
                                        <option key={sub._id || sub.name} value={sub.name}>
                                            {sub.name}
                                        </option>
                                    ))}
                                </select>

                                {/* Brand Filter */}
                                <select
                                    value={brandFilter}
                                    onChange={(e) => setBrandFilter(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ab3d22] focus:border-transparent bg-white"
                                >
                                    <option value="all">All Brands</option>
                                    {brands.map(brand => (
                                        <option key={brand} value={brand}>
                                            {brand}
                                        </option>
                                    ))}
                                </select>

                                {/* Sort */}
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ab3d22] focus:border-transparent bg-white"
                                >
                                    <option value="rating">Highest Rated</option>
                                    <option value="reviews"># of Reviews</option>
                                    <option value="repurchase">% Repurchase</option>
                                    <option value="recent">Most Recent</option>
                                    <option value="name">A-Z</option>
                                </select>
                            </div>

                            {/* Clear Filters */}
                            {(searchTerm || categoryFilter !== 'all' || subcategoryFilter !== 'all' || brandFilter !== 'all' || sortBy !== 'rating') && (
                                <button
                                    onClick={clearFilters}
                                    className="px-4 py-2 text-sm text-gray-600 hover:text-[#ab3d22] transition-colors whitespace-nowrap"
                                >
                                    Clear All
                                </button>
                            )}
                        </div>

                        {/* Results Count */}
                        <div className="mt-3 text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredProducts.length}</span> products
                        </div>
                    </div>
                </Container>
            </div>

            {/* Products List */}
            <Container>
                <div className="py-8">
                    {paginatedProducts.length > 0 ? (
                        <>
                            <div className="space-y-4">
                                {paginatedProducts.map((product) => (
                                    <ProductRow key={product._id || product.id} product={product} />
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-8 flex justify-center items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    
                                    <div className="flex gap-1">
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }
                                            
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`px-4 py-2 text-sm font-medium rounded-lg ${
                                                        currentPage === pageNum
                                                            ? 'bg-[#ab3d22] text-white'
                                                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        /* Empty State */
                        <div className="text-center py-16">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Search className="h-10 w-10 text-gray-400" />
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                                Couldn't find your product?
                            </h3>
                            <p className="text-gray-600 mb-6 max-w-md mx-auto">
                                We're constantly adding new products to our database. If you can't find what you're looking for, let us know!
                            </p>
                            <button
                                onClick={() => {
                                    // TODO: Open modal for product submission
                                    alert('Product submission feature coming soon! For now, please contact us through our contact page.');
                                }}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#ab3d22] text-white text-base font-medium rounded-lg hover:bg-[#8a2f1a] transition-colors shadow-md hover:shadow-lg"
                            >
                                <Plus className="h-5 w-5" />
                                Add a Product
                            </button>
                            
                            {(searchTerm || categoryFilter !== 'all' || brandFilter !== 'all') && (
                                <button
                                    onClick={clearFilters}
                                    className="block mx-auto mt-4 text-[#ab3d22] hover:underline text-sm font-medium"
                                >
                                    Clear filters and see all products
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </Container>

            {/* The Radiance Difference Section */}
            <section className="bg-[#f8f8f8]">
                <Container className="py-8 sm:py-10">
                    <div className="flex flex-col lg:flex-row items-center justify-center max-w-6xl mx-auto">
                        <div className="flex justify-center lg:w-1/2 mb-8 lg:mb-0 lg:pr-8">
                            <img
                                src="/images/radiancedifference.png"
                                alt="Woman with radiant, glowing skin applying skincare"
                                className="w-full max-w-[450px] h-auto rounded-3xl"
                            />
                        </div>
                        <div className="lg:w-1/2 text-center lg:text-left">
                            <div className="mb-4 text-sm tracking-widest text-gray-500 uppercase">NEWSLETTER</div>
                            <h2 className="text-3xl md:text-4xl mb-6" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                                <span className="text-gray-900">The Radiance</span> <span className="text-[#ab3d22]">Difference</span>
                            </h2>
                            <div className="space-y-6 max-w-2xl mx-auto lg:mx-0">
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    The internet is filled with praise for every product—but which ones actually work? Many users share your frustration with unreliable reviews, paid promotions, and information overload.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    Choosing beauty and wellness products should be simple and trustworthy—not a source of stress.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    Radiance is your go-to hub for beauty solutions that genuinely work—for everyone.
                                </p>
                                <a 
                                    href="http://eepurl.com/jpTPXQ"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block"
                                >
                                    <button
                                        type="button"
                                        className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-base font-semibold text-white bg-[#ab3d22] hover:bg-[#8a2f1a] focus:outline-none focus:ring-2 focus:ring-[#ab3d22] focus:ring-offset-2 transition-all duration-200"
                                    >
                                        Subscribe Now
                                    </button>
                                </a>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            <Footer />
        </div>
    );
};

export default AllProductsPage;

